import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ClipboardCheck } from "@/components/Icon";
import { Button } from "@/components/ui/button";
import { useQuoteCart } from "@/contexts/QuoteCart";
import TopBar from "./TopBar";
import logoZwart from "@/assets/logo-magic-print.webp";

type NavLink = {
  label: string;
  href: string;
  children?: { label: string; href: string }[];
};

const navLinks: NavLink[] = [
  { label: "Home", href: "/" },
  { label: "Producten", href: "/producten" },
  {
    label: "Druktechnieken",
    href: "/druktechnieken",
    children: [
      { label: "Alle druktechnieken", href: "/druktechnieken" },
      { label: "Borduren", href: "/druktechnieken/borduren" },
      { label: "DTF Printing", href: "/druktechnieken/dtf-printing" },
      { label: "Zeefdruk", href: "/druktechnieken/zeefdrukkerij" },
      { label: "Transferdruk", href: "/druktechnieken/transferdruk" },
    ],
  },
  { label: "Textiel bedrukken", href: "/textiel-bedrukken" },
  { label: "Over ons", href: "/over-ons" },
  { label: "Aanbiedingen", href: "/aanbiedingen" },
  { label: "Contact", href: "/contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [mobileOpenSub, setMobileOpenSub] = useState<string | null>(null);
  const location = useLocation();
  const { count, open: openCart } = useQuoteCart();

  const handleNavClick = (href: string) => {
    setOpen(false);
    if (href.startsWith("/#")) {
      const id = href.slice(2);
      if (location.pathname === "/") {
        const el = document.getElementById(id);
        el?.scrollIntoView({ behavior: "smooth" });
      } else {
        window.location.href = href;
      }
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-border">
      <TopBar />
      <div className="container flex items-center justify-between h-16 md:h-20 gap-4">
        <Link to="/" className="flex items-center shrink-0">
          <img
            src={logoZwart}
            alt="Magic Print logo"
            width={268}
            height={56}
            className="h-8 md:h-10 w-auto object-contain"
          />
        </Link>

        {/* Desktop */}
        <div className="hidden xl:flex items-center flex-1 justify-center">
          {navLinks.map((l, idx) => {
            const isActive =
              l.href === "/"
                ? location.pathname === "/"
                : location.pathname === l.href ||
                  (l.children && location.pathname.startsWith(l.href + "/"));
            const baseClasses = `relative text-sm font-bold uppercase tracking-wide whitespace-nowrap transition-colors ${
              isActive
                ? "text-[#059594] after:absolute after:left-0 after:right-0 after:-bottom-2 after:h-[2px] after:bg-[#059594]"
                : "text-foreground hover:text-[#059594]"
            }`;
            return (
              <div key={l.href} className="flex items-center">
                {idx > 0 && (
                  <span className="mx-3 2xl:mx-5 flex flex-col gap-1" aria-hidden="true">
                    <span className="w-1 h-1 rounded-full bg-[#059594]" />
                    <span className="w-1 h-1 rounded-full bg-[#059594]" />
                    <span className="w-1 h-1 rounded-full bg-[#059594]" />
                  </span>
                )}
                {l.children ? (
                  <div className="relative group">
                    <Link
                      to={l.href}
                      className={`${baseClasses} flex items-center gap-1`}
                    >
                      {l.label}
                      <ChevronDown className="w-3.5 h-3.5" />
                    </Link>
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all min-w-[220px] z-50">
                      <div className="bg-white border border-border rounded-lg shadow-lg py-2">
                        {l.children.map((c) => (
                          <Link
                            key={c.href}
                            to={c.href}
                            className="block px-4 py-2 text-sm uppercase font-medium text-foreground hover:bg-muted hover:text-[#059594] transition-colors"
                          >
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : l.href.startsWith("/#") ? (
                  <a
                    href={l.href}
                    onClick={(e) => {
                      if (location.pathname === "/") {
                        e.preventDefault();
                        handleNavClick(l.href);
                      }
                    }}
                    className={baseClasses}
                  >
                    {l.label}
                  </a>
                ) : (
                  <Link to={l.href} className={baseClasses}>
                    {l.label}
                  </Link>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Mobile toggle - shown first on mobile so Offerte sits to its right */}
          <button
            className="xl:hidden p-2 order-1"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          <button
            type="button"
            onClick={openCart}
            className="relative inline-flex items-center gap-2 px-3 py-2 rounded-full hover:bg-muted transition order-2"
            aria-label="Offerte-aanvraag openen"
            title="Offerte-aanvraag"
          >
            <ClipboardCheck className="w-5 h-5 text-[#059594]" />
            <span className="hidden md:inline text-sm font-bold uppercase tracking-wide text-foreground">
              Gratis Offerte
            </span>
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-[#059594] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="xl:hidden bg-background border-b border-border animate-fade-in">
          <div className="container py-4 flex flex-col gap-1">
            {navLinks.map((l) => {
              if (l.children) {
                const isOpen = mobileOpenSub === l.href;
                return (
                  <div key={l.href} className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => setMobileOpenSub(isOpen ? null : l.href)}
                      className="flex items-center justify-between py-2 text-sm font-medium uppercase text-muted-foreground hover:text-foreground"
                    >
                      {l.label}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    {isOpen && (
                      <div className="flex flex-col pl-3 border-l border-border ml-1 mb-2">
                        {l.children.map((c) => (
                          <Link
                            key={c.href}
                            to={c.href}
                            onClick={() => setOpen(false)}
                            className="py-2 text-sm uppercase text-muted-foreground hover:text-foreground"
                          >
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return l.href.startsWith("/#") ? (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => {
                    if (location.pathname === "/") {
                      e.preventDefault();
                    }
                    handleNavClick(l.href);
                  }}
                  className="text-sm font-medium py-2 uppercase text-muted-foreground hover:text-foreground"
                >
                  {l.label}
                </a>
              ) : (
                <Link
                  key={l.href}
                  to={l.href}
                  onClick={() => setOpen(false)}
                  className="text-sm font-medium py-2 uppercase text-muted-foreground hover:text-foreground"
                >
                  {l.label}
                </Link>
              );
            })}
            <Button asChild className="mt-2 uppercase">
              <Link to="/offerte">Gratis offerte aanvragen</Link>
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
